import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "@/lib/api";
import { Trophy, Flame, Target, Gamepad2, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import PlatformBadge from "@/components/PlatformBadge";
import { AdBanner } from "@/components/AdSense";
import { DashboardSkeleton } from "@/components/Skeleton";
import MultiplayerWidget from "@/components/Dashboard/MultiplayerWidget";
import WhatsNewStrip from "@/components/Dashboard/WhatsNewStrip";

import { getApiUrl } from "@/lib/api";
const API = `${getApiUrl()}/api`;

const PLATFORM_COLORS = {
  xbox: "#107C10",
  playstation: "#003087",
  steam: "#66C0F4",
  epic: "#FFFFFF",
};

const PLATFORM_ABBRS = {
  xbox: "GS",
  playstation: "XP",
  steam: "XP",
  epic: "XP",
};

// Helper function to format hours into months, days, and hours
const formatHoursToReadable = (hours) => {
  if (!hours || hours <= 0) return "0h";
  
  const totalHours = Math.round(hours);
  const hoursPerDay = 24;
  const daysPerMonth = 30;
  
  const months = Math.floor(totalHours / (hoursPerDay * daysPerMonth));
  const remainingAfterMonths = totalHours % (hoursPerDay * daysPerMonth);
  const days = Math.floor(remainingAfterMonths / hoursPerDay);
  const remainingHours = remainingAfterMonths % hoursPerDay;
  
  const parts = [];
  if (months > 0) parts.push(`${months}mo`);
  if (days > 0) parts.push(`${days}d`);
  if (remainingHours > 0 || parts.length === 0) parts.push(`${remainingHours}h`);
  
  return parts.join(" ");
};

// Format a range of hours
const formatHoursRange = (min, max, avg) => {
  if (min === max) {
    return formatHoursToReadable(avg);
  }
  return `${formatHoursToReadable(min)} - ${formatHoursToReadable(max)}`;
};

// --- Independent Flipping Tile Infrastructure ---

// Responsive column count hook — mirrors Tailwind breakpoints
const ACH_BREAKPOINTS = [[1280, 4], [1024, 3], [640, 2]];
const GAMES_BREAKPOINTS = [[1024, 3], [640, 2]];

function useResponsiveColumns(breakpoints) {
  const getColumns = useCallback(() => {
    const w = window.innerWidth;
    for (const [min, cols] of breakpoints) {
      if (w >= min) return cols;
    }
    return 1;
  }, [breakpoints]);

  const [columns, setColumns] = useState(getColumns);

  useEffect(() => {
    const onResize = () => setColumns(getColumns());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [getColumns]);

  return columns;
}

// Round-robin distribute items into N lanes
function distributeLanes(items, numLanes) {
  const lanes = Array.from({ length: numLanes }, () => []);
  items.forEach((item, i) => lanes[i % numLanes].push(item));
  return lanes;
}

// Single flipping tile — cycles through its own lane independently, pauses on hover
function FlipTile({ items, delay, renderContent }) {
  const [index, setIndex] = useState(0);
  const [flipping, setFlipping] = useState(false);
  const flipRef = useRef(false);
  const hoveredRef = useRef(false);

  useEffect(() => {
    if (items.length <= 1) return;
    let timer;
    const start = setTimeout(() => {
      timer = setInterval(() => {
        if (!hoveredRef.current && !flipRef.current) {
          flipRef.current = true;
          setFlipping(true);
          // Wait for flip-out to complete (500ms transition), pause briefly, then swap content and flip back in
          setTimeout(() => {
            setIndex((prev) => (prev + 1) % items.length);
            setTimeout(() => {
              setFlipping(false);
              flipRef.current = false;
            }, 150);
          }, 500);
        }
      }, 10000);
    }, delay);
    return () => {
      clearTimeout(start);
      clearInterval(timer);
    };
  }, [items.length, delay]);

  const item = items[index];
  if (!item) return null;

  return (
    <div
      style={{ perspective: "800px" }}
      onMouseEnter={() => { hoveredRef.current = true; }}
      onMouseLeave={() => { hoveredRef.current = false; }}
    >
      <div
        className="transition-transform duration-500 ease-in-out"
        style={{
          transformStyle: "preserve-3d",
          transform: flipping ? "rotateY(90deg)" : "rotateY(0deg)",
        }}
      >
        {renderContent(item)}
      </div>
    </div>
  );
}

// --- Carousel Components ---

// Achievement Carousel — independently flipping tiles (responsive 4→3→2→1)
function AchievementCarousel({ achievements, onNavigate, getScoreAbbr }) {
  const columns = useResponsiveColumns(ACH_BREAKPOINTS);
  const lanes = distributeLanes(achievements, columns);

  return (
    <div
      className="grid gap-3"
      style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
      data-testid="achievement-carousel"
    >
      {lanes.map((lane, i) => (
        <FlipTile
          key={`ach-${columns}-${i}`}
          items={lane}
          delay={5000 + i * 1500}
          renderContent={(ach) => (
            <div
              className="bg-[#121212] rounded-xl border border-white/5 hover:border-white/20 transition-colors overflow-hidden cursor-pointer group"
              onClick={() => onNavigate(ach)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && onNavigate(ach)}
              data-testid={`achievement-tile-${ach.id}`}
            >
              {/* Game box art background + achievement icon overlay */}
              <div className="h-20 bg-black/40 overflow-hidden relative">
                {ach.gameBoxArtUrl ? (
                  <img
                    src={ach.gameBoxArtUrl}
                    alt=""
                    className="w-full h-full object-contain opacity-60 group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Gamepad2 className="w-6 h-6 text-white/10" />
                  </div>
                )}
                {ach.imageUrl && (
                  <img
                    src={ach.imageUrl}
                    alt=""
                    className="absolute bottom-1 right-1 w-10 h-10 rounded-lg border border-white/20 bg-black/60"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                )}
              </div>
              <div className="p-3">
                <p className="text-white font-semibold text-sm line-clamp-2 min-h-[2.5rem]" title={ach.name}>{ach.name}</p>
                <p className="text-white/40 text-xs truncate mt-0.5" title={ach.gameName}>{ach.gameName}</p>
                <div className="flex items-center justify-between mt-2">
                  <PlatformBadge platform={ach.platform} size="sm" />
                  <span className="text-yellow-400 font-mono text-xs">
                    {ach.score} {getScoreAbbr(ach.platform)}
                  </span>
                </div>
                {ach.earnedAt && (
                  <p className="text-white/20 text-[10px] mt-1">
                    {new Date(ach.earnedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </p>
                )}
              </div>
            </div>
          )}
        />
      ))}
    </div>
  );
}

// Recently Played Games — independently flipping tiles (responsive 3→2→1)
function RecentGamesCarousel({ games, onNavigate, getPlatformColor }) {
  const columns = useResponsiveColumns(GAMES_BREAKPOINTS);
  const lanes = distributeLanes(games, columns);

  return (
    <div
      className="grid gap-3"
      style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
      data-testid="recent-games-carousel"
    >
      {lanes.map((lane, i) => (
        <FlipTile
          key={`game-${columns}-${i}`}
          items={lane}
          delay={5000 + i * 2000}
          renderContent={(game) => (
            <div
              className="bg-[#121212] rounded-xl border border-white/5 hover:border-white/20 transition-colors overflow-hidden cursor-pointer group"
              onClick={() => onNavigate(game)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && onNavigate(game)}
              data-testid={`recent-game-${game.id}`}
            >
              <div className="h-28 bg-black/40 overflow-hidden">
                {game.boxArtUrl ? (
                  <img
                    src={game.boxArtUrl}
                    alt={game.name}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => { e.target.onerror = null; e.target.style.display = "none"; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Gamepad2 className="w-8 h-8 text-white/10" />
                  </div>
                )}
              </div>
              <div className="p-3">
                <p className="text-white font-semibold text-sm line-clamp-2 min-h-[2.5rem]" title={game.name}>{game.name}</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <PlatformBadge platform={game.platform} size="sm" />
                  {game.system && (
                    <span className="text-white/40 text-xs bg-white/5 px-1.5 py-0.5 rounded">{game.system}</span>
                  )}
                </div>
                <div className="mt-2">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-white/50">{game.completionPct}%</span>
                    <span className="text-cyan-400 font-mono text-[10px]">
                      {game.remainingHoursMin === game.remainingHoursMax
                        ? `${formatHoursToReadable(game.remainingHoursMax)} left`
                        : `${formatHoursToReadable(game.remainingHoursMin)}-${formatHoursToReadable(game.remainingHoursMax)} left`
                      }
                    </span>
                  </div>
                  <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${Math.min(game.completionPct, 100)}%`,
                        backgroundColor: getPlatformColor(game.platform),
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        />
      ))}
    </div>
  );
}

// Completed Games — single tile auto-cycling through recent completions
function CompletedGamesCarousel({ games, onNavigate, getScoreAbbr }) {
  return (
    <div data-testid="completed-games-carousel">
      <FlipTile
        items={games}
        delay={8000}
        renderContent={(game) => {
          const completedDate = game.completedAt
            ? new Date(game.completedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
            : null;

          let completionTime = null;
          if (game.firstUnlock && game.completedAt) {
            const diff = new Date(game.completedAt) - new Date(game.firstUnlock);
            const days = Math.floor(diff / 86400000);
            const hours = Math.floor((diff % 86400000) / 3600000);
            if (days > 0) completionTime = `${days}d ${hours}h`;
            else if (hours > 0) completionTime = `${hours}h`;
            else completionTime = "<1h";
          }

          const displayName = game.isDLC && game.parentGameName
            ? `${game.parentGameName} — ${game.name}`
            : game.name;

          return (
            <div
              className="bg-[#121212] rounded-xl border border-white/5 hover:border-white/20 transition-colors overflow-hidden cursor-pointer group"
              onClick={() => onNavigate(game)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && onNavigate(game)}
              data-testid={`completed-game-${game.id}`}
            >
              <div className="h-28 bg-black/40 overflow-hidden">
                {game.boxArtUrl ? (
                  <img
                    src={game.boxArtUrl}
                    alt={game.name}
                    className="w-full h-full object-contain"
                    onError={(e) => { e.target.onerror = null; e.target.style.display = "none"; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Gamepad2 className="w-8 h-8 text-white/10" />
                  </div>
                )}
              </div>
              <div className="p-3">
                <p className="text-white font-semibold text-sm line-clamp-2 min-h-[2.5rem]" title={displayName}>{displayName}</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <PlatformBadge platform={game.platform} size="sm" />
                  {game.system && (
                    <span className="text-white/40 text-xs bg-white/5 px-1.5 py-0.5 rounded">{game.system}</span>
                  )}
                </div>
                <div className="flex items-center justify-between mt-2 text-xs">
                  <span className="text-yellow-400 font-mono font-semibold">
                    {game.earnedScore?.toLocaleString()} {getScoreAbbr(game.platform)}
                  </span>
                  {completionTime && (
                    <span className="text-cyan-400 font-mono">{completionTime}</span>
                  )}
                </div>
                <div className="flex items-center justify-between mt-1 text-[10px] text-white/30">
                  <span>{game.totalAchievements} achievements</span>
                  {completedDate && <span>{completedDate}</span>}
                </div>
              </div>
            </div>
          );
        }}
      />
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [platforms, setPlatforms] = useState([]);
  const [completionTimeStats, setCompletionTimeStats] = useState(null);
  const [recentCompletions, setRecentCompletions] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchPlatforms();
    fetchCompletionTimeStats();
    fetchRecentCompletions();
  }, []);

  const fetchStats = async () => {
    try {
      const tzOff = -new Date().getTimezoneOffset();
      const tzName = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await axios.get(`${API}/stats?tz_offset=${tzOff}&tz_name=${encodeURIComponent(tzName)}`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletionTimeStats = async () => {
    try {
      const response = await axios.get(`${API}/stats/completion-time`);
      setCompletionTimeStats(response.data);
    } catch (error) {
      console.error("Failed to fetch completion time stats:", error);
    }
  };

  const fetchPlatforms = async () => {
    try {
      const response = await axios.get(`${API}/platforms`);
      setPlatforms(response.data.filter(p => p.isEnabled !== false));
    } catch (error) {
      console.error("Failed to fetch platforms:", error);
    }
  };

  const fetchRecentCompletions = async () => {
    try {
      const response = await axios.get(`${API}/stats/recent-completions`);
      setRecentCompletions(response.data);
    } catch (error) {
      console.error("Failed to fetch recent completions:", error);
    }
  };

  const getScoreAbbr = (platformId) => {
    const platform = platforms.find(p => p.id === platformId);
    return platform?.scoreAbbr || PLATFORM_ABBRS[platformId] || "XP";
  };

  const getPlatformColor = (platformId) => {
    const platform = platforms.find(p => p.id === platformId);
    return platform?.color || PLATFORM_COLORS[platformId] || "#7C3AED";
  };

  const getPlatformName = (platformId) => {
    const platform = platforms.find(p => p.id === platformId);
    return platform?.name || platformId.charAt(0).toUpperCase() + platformId.slice(1);
  };

  if (loading) {
    return <DashboardSkeleton />;
  }

  // Platform breakdown data
  const platformData = stats?.platformBreakdown
    ? Object.entries(stats.platformBreakdown)
        .map(([platform, data]) => ({
          platform,
          name: getPlatformName(platform),
          score: data.score,
          count: data.count,
          color: getPlatformColor(platform),
          // New fields for progress tracking
          totalCount: data.totalCount || 0,
          totalScore: data.totalScore || 0,
          effectiveTotalCount: data.effectiveTotalCount || 0,
          effectiveTotalScore: data.effectiveTotalScore || 0,
          effectiveEarnedCount: data.effectiveEarnedCount || 0,
          effectiveEarnedScore: data.effectiveEarnedScore || 0,
          remainingCount: data.remainingCount || 0,
          remainingScore: data.remainingScore || 0,
          countPercentage: data.countPercentage || 0,
          scorePercentage: data.scorePercentage || 0,
        }))
        .sort((a, b) => b.score - a.score)
    : [];

  const completionPct = stats?.completionPercentage || 0;

  // Use backend stats for accurate game counts (gamesPlayed = games with at least 1 unlock).
  // DLC unlocks are rolled up to the parent base game backend-side, so gamesPlayed
  // is now guaranteed ≤ totalGames. Clamp to >=0 anyway as a UI safety net.
  const gamesPlayed = stats?.gamesPlayed || 0;
  const totalGames = stats?.totalGames || 0;
  const notStartedGames = Math.max(0, totalGames - gamesPlayed);

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-3xl md:text-4xl text-white tracking-tight">
          Dashboard
        </h1>
        <p className="text-white/50 mt-1">Your gaming achievement overview</p>
      </div>

      {/* What's New strip */}
      <WhatsNewStrip />

      {/* Expanded Stats Grid - 2 Columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Synced Score Card - Variable height for all platforms */}
        <Card className="bg-[#0A0A0A] border-white/5 card-hover animate-fade-in-up stagger-1" data-testid="stat-card-score">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="w-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-yellow-500/10 rounded-xl">
                    <Trophy className="w-8 h-8 text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm font-medium uppercase tracking-wider">Synced Score</p>
                    <p className="text-4xl font-bold text-white">{stats?.totalScore?.toLocaleString() || 0}</p>
                  </div>
                </div>
                
                {/* Score Completion Progress Bar */}
                {stats?.effectivePossibleScore > 0 && (
                  <div className="mb-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-white/50">Score Completion</span>
                      <span className="text-yellow-400 font-medium">{stats?.scoreCompletionPercentage || 0}%</span>
                    </div>
                    <Progress 
                      value={stats?.scoreCompletionPercentage || 0} 
                      className="h-3 [&>div]:bg-gradient-to-r [&>div]:from-yellow-500 [&>div]:to-yellow-400" 
                    />
                    <div className="flex items-center justify-between text-xs text-white/40">
                      <span>{(stats?.effectiveEarnedScore || 0).toLocaleString()} earned</span>
                      <span>
                        {((stats?.effectivePossibleScore || 0) - (stats?.effectiveEarnedScore || 0)).toLocaleString()} remaining
                        {stats?.excludedScore > 0 && (
                          <span className="text-orange-400/60 ml-1">
                            ({stats.excludedScore.toLocaleString()} excluded)
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Score by platform breakdown with progress bars */}
                <div className="space-y-3">
                  {platformData.filter(p => p.effectiveTotalScore > 0).map((platform) => (
                    <div key={platform.platform} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: platform.color }} 
                          />
                          <span className="text-white/60 text-sm">{platform.name}</span>
                        </div>
                        <span className="text-yellow-400/80 font-mono text-sm">
                          {platform.scorePercentage}%
                        </span>
                      </div>
                      <div className="relative h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="absolute inset-y-0 left-0 rounded-full transition-all duration-500"
                          style={{ 
                            width: `${Math.min(platform.scorePercentage, 100)}%`,
                            backgroundColor: platform.color 
                          }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-white/30">
                        <span>{platform.effectiveEarnedScore.toLocaleString()} {getScoreAbbr(platform.platform)}</span>
                        <span>{platform.remainingScore.toLocaleString()} remaining</span>
                      </div>
                    </div>
                  ))}
                  {platformData.filter(p => p.effectiveTotalScore > 0).length === 0 && (
                    <p className="text-white/30 text-sm">No scores yet</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Synced Achievements Card - Variable height for all platforms */}
        <Card className="bg-[#0A0A0A] border-white/5 card-hover animate-fade-in-up stagger-2" data-testid="stat-card-achievements">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-green-500/10 rounded-xl">
                    <Target className="w-8 h-8 text-green-400" />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm font-medium uppercase tracking-wider">Synced Achievements</p>
                    <p className="text-4xl font-bold text-white">
                      {stats?.unlockedAchievements?.toLocaleString() || 0}
                      <span className="text-white/30 text-xl font-normal ml-2">
                        / {stats?.totalAchievements?.toLocaleString() || 0}
                      </span>
                    </p>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">Completion Progress</span>
                    <span className="text-green-400 font-medium">{completionPct}%</span>
                  </div>
                  <Progress value={completionPct} className="h-3 [&>div]:bg-gradient-to-r [&>div]:from-green-500 [&>div]:to-emerald-400" />
                </div>
                {/* Achievement breakdown by platform with progress bars */}
                <div className="space-y-3 mt-4">
                  {platformData.filter(p => p.effectiveTotalCount > 0).map((platform) => (
                    <div key={platform.platform} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: platform.color }} 
                          />
                          <span className="text-white/60 text-sm">{platform.name}</span>
                        </div>
                        <span className="text-sm" style={{ color: platform.color }}>
                          {platform.countPercentage}%
                        </span>
                      </div>
                      <div className="relative h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="absolute inset-y-0 left-0 rounded-full transition-all duration-500"
                          style={{ 
                            width: `${Math.min(platform.countPercentage, 100)}%`,
                            backgroundColor: platform.color 
                          }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-white/30">
                        <span>{platform.effectiveEarnedCount.toLocaleString()} unlocked</span>
                        <span>{platform.remainingCount.toLocaleString()} remaining</span>
                      </div>
                    </div>
                  ))}
                  {platformData.filter(p => p.effectiveTotalCount > 0).length === 0 && (
                    <p className="text-white/30 text-sm">No achievements yet</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Games Played Card - With platform breakdown */}
        <Card className="bg-[#0A0A0A] border-white/5 card-hover animate-fade-in-up stagger-3" data-testid="stat-card-played">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-blue-500/10 rounded-xl">
                    <Gamepad2 className="w-8 h-8 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm font-medium uppercase tracking-wider">Games Played</p>
                    <p className="text-4xl font-bold text-white">
                      {stats?.gamesPlayed?.toLocaleString() || 0}
                      <span className="text-white/30 text-xl font-normal ml-2">
                        / {stats?.totalGames?.toLocaleString() || 0}
                      </span>
                    </p>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">Library Progress</span>
                    <span className="text-blue-400 font-medium">
                      {stats?.totalGames > 0 ? Math.round((stats.gamesPlayed / stats.totalGames) * 100) : 0}%
                    </span>
                  </div>
                  <Progress 
                    value={stats?.totalGames > 0 ? (stats.gamesPlayed / stats.totalGames) * 100 : 0} 
                    className="h-3 [&>div]:bg-gradient-to-r [&>div]:from-blue-500 [&>div]:to-cyan-400" 
                  />
                </div>
                {/* Overall stats */}
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="bg-[#121212] rounded-lg p-3 text-center">
                    <p className="text-white/40 text-xs">Not Started</p>
                    <p className="text-white font-bold text-lg">{notStartedGames}</p>
                  </div>
                  <div className="bg-[#121212] rounded-lg p-3 text-center">
                    <p className="text-white/40 text-xs">In Progress</p>
                    <p className="text-white font-bold text-lg">{gamesPlayed}</p>
                  </div>
                </div>
                {/* Per-platform game counts from gamesByPlatform */}
                {stats?.gamesByPlatform && Object.keys(stats.gamesByPlatform).length > 0 && (
                  <div className="space-y-2 mt-4">
                    <p className="font-heading text-base text-white flex items-center gap-2 mb-3">Games by Platform</p>
                    {Object.entries(stats.gamesByPlatform).map(([platform, data]) => (
                      <div key={platform} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: getPlatformColor(platform) }} 
                          />
                          <span className="text-white/60">{getPlatformName(platform)}</span>
                        </div>
                        <span className="text-white/80">
                          <span className="text-green-400">{data.played}</span>
                          <span className="text-white/30"> / {data.total}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Best Streak Card - Show all platforms + combined */}
        <Card className="bg-[#0A0A0A] border-white/5 card-hover animate-fade-in-up stagger-4" data-testid="stat-card-streak">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-orange-500/10 rounded-xl">
                    <Flame className="w-8 h-8 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm font-medium uppercase tracking-wider">Best Streak</p>
                    <p className="text-4xl font-bold text-white">
                      {stats?.longestStreak || 0}
                      <span className="text-white/30 text-xl font-normal ml-2">days</span>
                    </p>
                  </div>
                </div>
                {/* Current streak */}
                <div className="mt-4 space-y-4">
                  {stats?.currentStreak > 0 ? (
                    <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-lg p-4 border border-orange-500/30">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">🔥</span>
                        <div>
                          <p className="text-orange-400 font-bold text-lg">{stats.currentStreak} day streak!</p>
                          <p className="text-white/50 text-sm">Keep it going!</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-[#121212] rounded-lg p-4">
                      <p className="text-white/40 text-sm">No active streak</p>
                      <p className="text-white/60 text-xs mt-1">Unlock an achievement to start a new streak!</p>
                    </div>
                  )}
                  {/* Combined Streak Stats */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-[#121212] rounded-lg p-3 text-center">
                      <p className="text-white/40 text-xs">Current (All)</p>
                      <p className="text-orange-400 font-bold text-lg">{stats?.currentStreak || 0}</p>
                    </div>
                    <div className="bg-[#121212] rounded-lg p-3 text-center">
                      <p className="text-white/40 text-xs">Best (All)</p>
                      <p className="text-white font-bold text-lg">{stats?.longestStreak || 0}</p>
                    </div>
                  </div>
                  {/* Streak info */}
                  <p className="text-white/30 text-xs text-center">
                    Streaks are calculated across all platforms combined
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Estimated Completion Time Card - Full Width */}
      {completionTimeStats && completionTimeStats.gamesWithEstimates > 0 && (
        <Card className="bg-[#0A0A0A] border-white/5 card-hover animate-fade-in-up stagger-5" data-testid="stat-card-completion-time">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-cyan-500/10 rounded-xl">
                <Clock className="w-8 h-8 text-cyan-400" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-white/70 text-sm font-medium uppercase tracking-wider">Estimated Time Remaining</p>
                    <p className="text-3xl font-bold text-cyan-400">
                      {formatHoursRange(
                        completionTimeStats.remainingHours.min,
                        completionTimeStats.remainingHours.max,
                        completionTimeStats.remainingHours.avg
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-white/40 text-xs">Across {completionTimeStats.gamesWithEstimates} games</p>
                    <p className="text-white/60 text-sm">
                      {completionTimeStats.remainingSessions.min === completionTimeStats.remainingSessions.max
                        ? `~${Math.round(completionTimeStats.remainingSessions.avg)} sessions`
                        : `${Math.round(completionTimeStats.remainingSessions.min)}-${Math.round(completionTimeStats.remainingSessions.max)} sessions`}
                      <span className="text-white/30 text-xs ml-1">
                        ({completionTimeStats.remainingSessions.sessionLength}h each)
                      </span>
                    </p>
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {/* Remaining Achievements */}
                  <div className="bg-[#121212] rounded-lg p-3 text-center">
                    <p className="text-white/40 text-xs">Achievements Left</p>
                    <p className="text-green-400 font-bold text-lg">{completionTimeStats.remainingAchievements.toLocaleString()}</p>
                  </div>
                  
                  {/* Remaining Score */}
                  <div className="bg-[#121212] rounded-lg p-3 text-center">
                    <p className="text-white/40 text-xs">Score Left</p>
                    <p className="text-yellow-400 font-bold text-lg">{completionTimeStats.remainingScore.toLocaleString()}</p>
                  </div>
                  
                  {/* Achievements per Hour */}
                  <div className="bg-[#121212] rounded-lg p-3 text-center">
                    <p className="text-white/40 text-xs">Ach/Hour</p>
                    <p className="text-green-400/80 font-bold text-lg">{completionTimeStats.ratesPerHour.achievements}</p>
                  </div>
                  
                  {/* Score per Hour */}
                  <div className="bg-[#121212] rounded-lg p-3 text-center">
                    <p className="text-white/40 text-xs">Score/Hour</p>
                    <p className="text-yellow-400/80 font-bold text-lg">{completionTimeStats.ratesPerHour.score.toLocaleString()}</p>
                  </div>
                  
                  {/* Games Breakdown */}
                  <div className="bg-[#121212] rounded-lg p-3 text-center">
                    <p className="text-white/40 text-xs">Games Status</p>
                    <div className="flex items-center justify-center gap-2 text-xs mt-1">
                      <span className="text-blue-400">{completionTimeStats.gamesInProgress} active</span>
                      <span className="text-white/30">•</span>
                      <span className="text-white/50">{completionTimeStats.gamesNotStarted} queued</span>
                    </div>
                  </div>
                </div>

                {/* Recently Played + Completed Games side by side */}
                {(completionTimeStats.recentlyPlayedGames?.length > 0 || recentCompletions.length > 0) && (
                  <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Recently Played - takes 2 columns */}
                    {completionTimeStats.recentlyPlayedGames?.length > 0 && (
                      <div className="lg:col-span-2">
                        <p className="font-heading text-base text-white flex items-center gap-2 mb-3">
                          <Gamepad2 className="w-4 h-4 text-cyan-400" />
                          Recently Played
                          <span className="text-white/30 text-sm font-normal">({completionTimeStats.recentlyPlayedGames.length})</span>
                        </p>
                        <RecentGamesCarousel
                          games={completionTimeStats.recentlyPlayedGames}
                          onNavigate={(game) => navigate(`/games/${game.id}`)}
                          getPlatformColor={getPlatformColor}
                        />
                      </div>
                    )}
                    {/* Completed Games - takes 1 column */}
                    {recentCompletions.length > 0 && (
                      <div className={completionTimeStats.recentlyPlayedGames?.length > 0 ? "" : "lg:col-span-3"}>
                        <p className="font-heading text-base text-white flex items-center gap-2 mb-3">
                          <Target className="w-4 h-4 text-green-400" />
                          Recently Completed
                          <span className="text-white/30 text-sm font-normal">({recentCompletions.length})</span>
                        </p>
                        <CompletedGamesCarousel
                          games={recentCompletions}
                          onNavigate={(game) => navigate(`/games/${game.id}`)}
                          getScoreAbbr={getScoreAbbr}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Achievements - Flipping Card Carousel */}
      <Card className="bg-[#0A0A0A] border-white/5 animate-fade-in-up stagger-6">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-400" />
            Recent Achievements
            {stats?.recentAchievements?.length > 0 && (
              <span className="text-white/30 text-sm font-normal ml-1">
                ({stats.recentAchievements.length})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.recentAchievements?.length > 0 ? (
            <AchievementCarousel
              achievements={stats.recentAchievements}
              onNavigate={(ach) => navigate(`/games/${ach.gameId}`)}
              getScoreAbbr={getScoreAbbr}
            />
          ) : (
            <div className="text-center py-12 text-white/30">
              <Trophy className="w-12 h-12 mx-auto mb-4 opacity-20" />
              <p>No achievements unlocked yet</p>
              <p className="text-sm mt-1">Start adding games and tracking achievements!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Multiplayer widget */}
      <MultiplayerWidget />

      {/* AdSense */}
      <AdBanner />
    </div>
  );
}
